import type { OvenTriggerRule, SensorReading } from '../api/client';

const MAX_SAMPLE_AGE_MS = 90_000;

interface TriggerState {
  context: string;
  time: number;
  value: number;
  armed: boolean;
  count: number;
}

function meetsCondition(rule: OvenTriggerRule, value: number, previous?: number): boolean {
  switch (rule.operator) {
    case 'greater_than': return value > rule.threshold;
    case 'greater_than_or_equal': return value >= rule.threshold;
    case 'less_than': return value < rule.threshold;
    case 'less_than_or_equal': return value <= rule.threshold;
    case 'equal': return value === rule.threshold;
    case 'not_equal': return value !== rule.threshold;
    case 'increase_by': return previous !== undefined && value - previous >= rule.threshold;
    case 'decrease_by': return previous !== undefined && previous - value >= rule.threshold;
  }
}

export class OvenTriggerTracker {
  private states = new Map<string, TriggerState>();

  reset(): void {
    this.states.clear();
  }

  evaluate(
    key: string,
    rule: OvenTriggerRule,
    reading: SensorReading | undefined,
    batchId: string | null,
    now = Date.now(),
  ): boolean {
    const time = reading ? Date.parse(reading.time) : NaN;
    const value = reading ? Number(reading.value) : NaN;
    if (!rule.enabled || !batchId || !reading || !reading.value.trim()
      || !Number.isFinite(value) || !Number.isFinite(time)
      || time > now || now - time > MAX_SAMPLE_AGE_MS) {
      this.states.delete(key);
      return false;
    }

    const context = JSON.stringify([batchId, reading.device_id, rule]);
    let state = this.states.get(key);
    if (!state || state.context !== context || time - state.time > MAX_SAMPLE_AGE_MS) {
      // A batch, device, or rule change establishes a baseline, not a roast event.
      state = {
        context, time: now, value, count: 0,
        armed: !meetsCondition(rule, value),
      };
      this.states.set(key, state);
      return false;
    }
    if (time <= state.time) return false;

    const satisfied = meetsCondition(rule, value, state.value);
    state.time = time;
    state.value = value;
    if (!satisfied) {
      state.count = 0;
      state.armed = true;
      return false;
    }
    if (!state.armed) return false;

    state.count += 1;
    if (state.count < rule.consecutive_samples_required) return false;
    state.count = 0;
    state.armed = false;
    return true;
  }
}
